// src/components/product/CollectionSelectModal.tsx
import * as React from "react";
import { Modal, Table, Input, Typography, Empty, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getCollectionHierarchyApi } from "src/api/collection/collectionApi";
import type { CollectionNode } from "src/types/collection/collectionTypes";
import CustomButton from "src/components/common/CustomButton";

export type SelectedCollectionItem = {
  collectionId: number;
  collectionName: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  onClose: () => void;
  onConfirm: (collections: SelectedCollectionItem[]) => void;
};

// 선택된 콜렉션 정보를 저장하기 위한 Map (ID -> 이름 매핑)
const collectionCache = new Map<number, string>();

// 계층 구조에서 모든 콜렉션을 캐시에 저장하는 함수
const cacheCollections = (nodes: CollectionNode[]) => {
  const cache = (node: CollectionNode) => {
    collectionCache.set(node.collectionId, node.collectionName);
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => cache(child));
    }
  };
  nodes.forEach((node) => cache(node));
};

export default function CollectionSelectModal({ open, selectedIds, onClose, onConfirm }: Props) {
  const [list, setList] = React.useState<CollectionNode[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [size] = React.useState(10);
  const [totalElements, setTotalElements] = React.useState(0);
  const [searchValue, setSearchValue] = React.useState("");
  const [tempSelectedIds, setTempSelectedIds] = React.useState<number[]>(selectedIds);
  const [selectedCollectionMap, setSelectedCollectionMap] = React.useState<
    Map<number, SelectedCollectionItem>
  >(new Map());

  React.useEffect(() => {
    if (open) {
      setTempSelectedIds(selectedIds);
      setPage(0);
      setSearchValue("");
      setSelectedCollectionMap(new Map());
    }
  }, [open, selectedIds]);

  React.useEffect(() => {
    if (!open) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, searchValue]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getCollectionHierarchyApi({
        collectionName: searchValue || undefined,
        page,
        size,
        order: "displayOrder,asc",
      });
      if (res.statusCode === 1 && res.data) {
        const hierarchicalList = res.data.content;
        setList(hierarchicalList);
        // 모든 콜렉션을 캐시에 저장
        cacheCollections(hierarchicalList);
        setTotalElements(res.data.page.totalElements);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
  };

  const handleConfirm = () => {
    const selectedCollections: SelectedCollectionItem[] = tempSelectedIds
      .map((id) => {
        // Map에서 먼저 확인
        if (selectedCollectionMap.has(id)) {
          return selectedCollectionMap.get(id)!;
        }
        // Map에 없으면 캐시에서 찾기
        const name = collectionCache.get(id);
        if (name) {
          return {
            collectionId: id,
            collectionName: name,
          };
        }
        return null;
      })
      .filter((item): item is SelectedCollectionItem => item !== null);
    onConfirm(selectedCollections);
    onClose();
  };

  // 노드를 찾는 재귀 함수
  const findNodeById = (nodes: CollectionNode[], id: number): CollectionNode | null => {
    for (const node of nodes) {
      if (node.collectionId === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // 선택 변경 핸들러
  const handleSelectionChange = (selectedRowKeys: React.Key[]) => {
    const newSelectedIds = selectedRowKeys as number[];
    setTempSelectedIds(newSelectedIds);

    // 선택된 ID들의 이름을 Map에 저장
    const newMap = new Map<number, SelectedCollectionItem>();
    newSelectedIds.forEach((id) => {
      const node = findNodeById(list, id);
      if (node) {
        newMap.set(id, {
          collectionId: node.collectionId,
          collectionName: node.collectionName,
        });
      } else {
        // 캐시에서 찾기
        const name = collectionCache.get(id);
        if (name) {
          newMap.set(id, {
            collectionId: id,
            collectionName: name,
          });
        }
      }
    });
    setSelectedCollectionMap(newMap);
  };

  const columns: ColumnsType<CollectionNode> = [
    {
      title: "콜렉션명",
      dataIndex: "collectionName",
      key: "collectionName",
      width: 250,
    },
    {
      title: "ID",
      dataIndex: "collectionId",
      key: "collectionId",
      width: 80,
      align: "center",
    },
    {
      title: "콜렉션 코드",
      dataIndex: "collectionCode",
      key: "collectionCode",
      width: 150,
    },
    {
      title: "깊이",
      dataIndex: "depth",
      key: "depth",
      width: 80,
      align: "center",
    },
    {
      title: "활성 여부",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      align: "center",
      render: (value: boolean) => (
        <Tag color={value ? "green" : undefined}>{value ? "ACTIVE" : "INACTIVE"}</Tag>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      centered
      title={<Typography.Text strong>콜렉션 선택</Typography.Text>}
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose}>
          취소
        </CustomButton>,
        <CustomButton key="clear" variant="outlined" onClick={() => setTempSelectedIds([])}>
          전체 해제
        </CustomButton>,
        <CustomButton key="confirm" variant="contained" onClick={handleConfirm}>
          확인 ({tempSelectedIds.length}개 선택)
        </CustomButton>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="콜렉션명 검색"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={list}
        loading={loading}
        rowKey="collectionId"
        pagination={{
          current: page + 1,
          pageSize: size,
          total: totalElements,
          onChange: (p) => setPage(p - 1),
          showSizeChanger: false,
        }}
        expandable={{
          childrenColumnName: "children",
          defaultExpandAllRows: false,
        }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: tempSelectedIds,
          onChange: handleSelectionChange,
          checkStrictly: true,
        }}
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="콜렉션이 없습니다." /> }}
      />
    </Modal>
  );
}
